// Shared Silent Auction lifecycle helpers.
// Keeps public and admin status labels, visibility, and timing decisions aligned.

export const AUCTION_STATUS = Object.freeze({
  DRAFT: "draft",
  STARTING_SOON: "starting_soon",
  OPEN_FOR_BIDDING: "open_for_bidding",
  FINISHED: "finished",
});

export const toAuctionDate = (value) => {
  if (!value) return null;
  if (typeof value.toDate === "function") return value.toDate();
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

export const getAuctionStatus = ({ item = {}, event = {}, now = new Date() } = {}) => {
  const itemStatus = String(item.status || "draft").toLowerCase();
  const start = toAuctionDate(item.auctionStart || event.auctionStart);
  const end = toAuctionDate(item.auctionEnd || event.auctionEnd);

  if (itemStatus === "draft") return AUCTION_STATUS.DRAFT;
  if (itemStatus === "closed" || (end && now >= end)) return AUCTION_STATUS.FINISHED;
  if (item.published === false) return AUCTION_STATUS.DRAFT;
  if (item.biddingStartsImmediately !== true && start && now < start) return AUCTION_STATUS.STARTING_SOON;
  return itemStatus === "open" ? AUCTION_STATUS.OPEN_FOR_BIDDING : AUCTION_STATUS.DRAFT;
};

export const getAuctionStatusLabel = (status) => ({
  [AUCTION_STATUS.DRAFT]: "Draft",
  [AUCTION_STATUS.STARTING_SOON]: "Starting Soon",
  [AUCTION_STATUS.OPEN_FOR_BIDDING]: "Open for Bidding",
  [AUCTION_STATUS.FINISHED]: "Auction Finished",
}[status] || "Draft");

export const isPublicAuctionItem = (status) => [
  AUCTION_STATUS.STARTING_SOON,
  AUCTION_STATUS.OPEN_FOR_BIDDING,
  AUCTION_STATUS.FINISHED,
].includes(status);

// Match the callable's cents-first pricing, including legacy dollar aliases.
export const getMinimumAuctionBidCents = (item = {}) => {
  const current = Number(item.currentBidCents ?? Math.round(Number(item.currentBid || 0) * 100));
  const starting = Number(item.startingBidCents ?? Math.round(Number(item.startingBid || 0) * 100));
  const increment = Math.max(1, Number(item.minimumIncrementCents ?? item.bidIncrementCents ??
    Math.round(Number(item.minimumIncrement || 1) * 100)));
  return current > 0 ? current + increment : Math.max(1, starting);
};

export const getAuctionCountdown = (start, now = new Date()) => {
  const date = toAuctionDate(start);
  const seconds = Math.max(0, Math.ceil(((date?.getTime() || 0) - now.getTime()) / 1000));
  const days = Math.floor(seconds / 86400);
  const clock = [Math.floor(seconds / 3600) % 24, Math.floor(seconds / 60) % 60, seconds % 60]
    .map((part) => String(part).padStart(2, "0")).join(":");
  return `${days}d ${clock}`;
};
